import React, { useEffect, useState, useMemo } from 'react';
import { Calendar, MapPin, Clock, ArrowRight, Play, Upload, Music, FileDown } from 'lucide-react';
import { EVENTS, HERO_IMAGES } from '../mock';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import VideoModal from '../components/VideoModal';
import TeachingUploadDialog from '../components/TeachingUploadDialog';
import api, { fileUrl } from '../lib/api';

export default function Events() {
  const [teachings, setTeachings] = useState([]);
  const [videoOpen, setVideoOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [defaultSeries, setDefaultSeries] = useState('');

  const load = () => {
    api.listTeachings().then(setTeachings).catch(() => setTeachings([]));
  };
  useEffect(load, []);

  const byEvent = useMemo(() => {
    const m = {};
    for (const e of EVENTS) {
      m[e.series] = teachings.filter(t => (t.series || '').toLowerCase() === e.series.toLowerCase());
    }
    return m;
  }, [teachings]);

  const openUpload = (series) => { setDefaultSeries(series); setUploadOpen(true); };

  const openVideo = (t) => {
    setActive({ videoUrl: fileUrl(t.video_url), title: t.title, isLocal: true });
    setVideoOpen(true);
  };

  return (
    <div>
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.congregation2} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B27]/95 via-[#0B1B27]/80 to-[#0B1B27]/50" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-white">
          <p className="text-xs tracking-[0.35em] uppercase text-gold-brand mb-4">Gatherings</p>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-tight">Come, be with us.</h1>
          <p className="mt-6 text-lg md:text-xl text-white/80 max-w-3xl">
            Weekly services, midweek prayer, and special gatherings &mdash; where the Word is opened and Jesus is worshipped.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ivory">
        <div className="max-w-6xl mx-auto px-4 md:px-6 space-y-8">
          {EVENTS.map(e => {
            const recordings = byEvent[e.series] || [];
            return (
              <Card key={e.id} className="p-6 md:p-8 bg-white rounded-2xl border border-ink/5 hover-lift">
                <div className="grid md:grid-cols-12 gap-6 items-start">
                  <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-3 md:gap-2">
                    <div className="h-16 w-16 rounded-2xl bg-teal-deep flex items-center justify-center">
                      <Calendar className="h-7 w-7 text-gold-brand" strokeWidth={1.5} />
                    </div>
                    <Badge className="bg-cream text-teal-deep border border-teal-brand/20">{e.tag}</Badge>
                  </div>
                  <div className="md:col-span-7">
                    <h3 className="font-serif-display text-2xl md:text-3xl text-ink">{e.title}</h3>
                    <p className="mt-2 text-ink/70 leading-relaxed">{e.description}</p>
                    <div className="mt-3 flex flex-wrap gap-4 text-sm text-ink/60">
                      <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {e.date}</span>
                      <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {e.time}</span>
                      <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {e.location}</span>
                    </div>
                  </div>
                  <div className="md:col-span-3 flex flex-col gap-2 md:items-end">
                    <Link to="/contact">
                      <Button className="bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold h-10 px-4 w-full md:w-auto">
                        RSVP <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    </Link>
                    <Button onClick={() => openUpload(e.series)} variant="outline" className="border-teal-brand text-teal-deep hover:bg-teal-brand hover:text-white h-10 px-4 w-full md:w-auto">
                      <Upload className="h-4 w-4 mr-1.5" /> Upload Recording
                    </Button>
                  </div>
                </div>

                {recordings.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-ink/5">
                    <p className="text-xs tracking-[0.25em] uppercase text-gold-deep font-medium mb-4">Recordings ({recordings.length})</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {recordings.map(t => (
                        <div key={t.id} className="group rounded-xl overflow-hidden bg-cream/50 border border-ink/5 hover-lift">
                          <div className="relative aspect-video bg-black cursor-pointer" onClick={() => t.video_url && openVideo(t)}>
                            {t.thumbnail_url ? (
                              <img src={fileUrl(t.thumbnail_url)} alt={t.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-teal-deep to-[#0B1B27] flex items-center justify-center">
                                <span className="font-serif-display text-white/80 text-xl px-4 text-center">{t.title.slice(0, 40)}</span>
                              </div>
                            )}
                            {t.video_url && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="h-12 w-12 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                  <Play className="h-5 w-5 text-teal-deep fill-teal-deep ml-0.5" />
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="p-3">
                            <p className="font-serif-display text-base text-ink leading-snug line-clamp-2">{t.title}</p>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {t.audio_url && <Badge variant="secondary" className="bg-cream text-teal-deep border border-teal-brand/20 text-[10px]">MP3</Badge>}
                              {t.pdf_url && <Badge variant="secondary" className="bg-cream text-gold-deep border border-gold-brand/20 text-[10px]">PDF</Badge>}
                              {t.duration && <span className="text-[10px] text-ink/50">{t.duration}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </section>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} videoUrl={active?.videoUrl} title={active?.title} isLocal={active?.isLocal} />
      <TeachingUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} defaultSeries={defaultSeries} onUploaded={load} />
    </div>
  );
}
